import React, { useState, useRef, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform, Animated, Easing, TextInput, Image } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import NavigationHeader from '../components/NavigationHeader';
import { BlurView } from 'expo-blur';
import { GlassBackgroundGradient, GradientOverlay } from '../components/Gradients';
import { ScreenshotIcon, SmallLogoIcon } from './HomeScreen';
import Dropdown from '../components/Dropdown';
import CommunityModal from '../components/CommunityModal';
import { useNavigation } from '../context/NavigationContext';
import { useTicker } from '../context/TickerContext';

const ExploreScreen = () => {
    const { navigateTo } = useNavigation();
    const { isTickerEnabled, totalHeaderHeight } = useTicker();
    
    // Page load animation
    const pageOpacity = useRef(new Animated.Value(0)).current;
    const pageTranslateY = useRef(new Animated.Value(20)).current;
    
    // Exit animation state
    const [isExiting, setIsExiting] = useState(false);
    
    // Community modal state
    const [showCommunityModal, setShowCommunityModal] = useState(false);
    
    // Image popup animations
    const card1ImagesOpacity = useRef(new Animated.Value(0)).current;
    const card1ImagesScale = useRef(new Animated.Value(0.8)).current;
    const card2ImagesOpacity = useRef(new Animated.Value(0)).current;
    const card2ImagesScale = useRef(new Animated.Value(0.8)).current;
    const card3ImagesOpacity = useRef(new Animated.Value(0)).current;
    const card3ImagesScale = useRef(new Animated.Value(0.8)).current;
    
    const card1GlowT = useRef(new Animated.Value(0)).current;
    const card2GlowT = useRef(new Animated.Value(0)).current;
    const card3GlowT = useRef(new Animated.Value(0)).current;
    
    // Individual image scale animations for each card
    const card1ImageScaleT = useRef(new Animated.Value(1)).current;
    const card2ImageScaleT = useRef(new Animated.Value(1)).current;
    const card3ImageScaleT = useRef(new Animated.Value(1)).current;
    
    // Position animations for Card 1 images (spread out effect)
    const poundTranslateT = useRef(new Animated.Value(0)).current;
    const dollarTranslateT = useRef(new Animated.Value(0)).current;
    const euroTranslateT = useRef(new Animated.Value(0)).current;
    
    // Position animations for Card 2 (rocket moves up and right)
    const rocketTranslateT = useRef(new Animated.Value(0)).current;
    
    // Position animations for Card 3 (community images spread apart)
    const boyTranslateT = useRef(new Animated.Value(0)).current;
    const girlTranslateT = useRef(new Animated.Value(0)).current;

    // Page load animation effect
    useEffect(() => {
        // Main page animation
        Animated.parallel([
            Animated.timing(pageOpacity, {
                toValue: 1,
                duration: 1200,
                easing: Easing.inOut(Easing.cubic),
                useNativeDriver: true,
            }),
            Animated.timing(pageTranslateY, {
                toValue: 0,
                duration: 1000,
                easing: Easing.inOut(Easing.cubic),
                useNativeDriver: true,
            }),
        ]).start();
    }, []);

    // Delayed image popup animation
    useEffect(() => {
        const timer = setTimeout(() => {
            Animated.stagger(200, [
                // Card 1 images
                Animated.parallel([
                    Animated.timing(card1ImagesOpacity, {
                        toValue: 1,
                        duration: 600,
                        easing: Easing.out(Easing.back(1.2)),
                        useNativeDriver: true,
                    }),
                    Animated.timing(card1ImagesScale, {
                        toValue: 1,
                        duration: 600,
                        easing: Easing.out(Easing.back(1.2)),
                        useNativeDriver: true,
                    }),
                ]),
                // Card 2 images
                Animated.parallel([
                    Animated.timing(card2ImagesOpacity, {
                        toValue: 1,
                        duration: 600,
                        easing: Easing.out(Easing.back(1.2)),
                        useNativeDriver: true,
                    }),
                    Animated.timing(card2ImagesScale, {
                        toValue: 1,
                        duration: 600,
                        easing: Easing.out(Easing.back(1.2)),
                        useNativeDriver: true,
                    }),
                ]),
                // Card 3 images
                Animated.parallel([
                    Animated.timing(card3ImagesOpacity, {
                        toValue: 1,
                        duration: 600,
                        easing: Easing.out(Easing.back(1.2)),
                        useNativeDriver: true,
                    }),
                    Animated.timing(card3ImagesScale, {
                        toValue: 1,
                        duration: 600,
                        easing: Easing.out(Easing.back(1.2)),
                        useNativeDriver: true,
                    }),
                ]),
            ]).start();
        }, 500); // Start after 500ms delay

        return () => clearTimeout(timer);
    }, []);

    // Register exit animation with navigation context
    useEffect(() => {
        // Store the exit animation function globally so navigation can access it
        if (Platform.OS === 'web') {
            window.exploreExitAnimation = exitAnimation;
        }
        
        return () => {
            // Clean up when component unmounts
            if (Platform.OS === 'web') {
                delete window.exploreExitAnimation;
            }
        };
    }, []);

    const onCard1In = () => {
        if (isExiting) return;
        Animated.timing(card1GlowT, { toValue: 1, duration: 1500, easing: Easing.bezier(0.22, 1, 0.36, 1), useNativeDriver: true }).start();
        Animated.timing(card1ImageScaleT, { toValue: 1.1, duration: 300, easing: Easing.bezier(0.25, 0.46, 0.45, 0.94), useNativeDriver: true }).start();
        // Spread out the currency images
        Animated.timing(poundTranslateT, { toValue: 1, duration: 300, easing: Easing.bezier(0.25, 0.46, 0.45, 0.94), useNativeDriver: true }).start();
        Animated.timing(dollarTranslateT, { toValue: 1, duration: 300, easing: Easing.bezier(0.25, 0.46, 0.45, 0.94), useNativeDriver: true }).start();
        Animated.timing(euroTranslateT, { toValue: 1, duration: 300, easing: Easing.bezier(0.25, 0.46, 0.45, 0.94), useNativeDriver: true }).start();
    };
    const onCard1Out = () => {
        if (isExiting) return;
        Animated.timing(card1GlowT, { toValue: 0, duration: 1500, easing: Easing.bezier(0.22, 1, 0.36, 1), useNativeDriver: true }).start();
        Animated.timing(card1ImageScaleT, { toValue: 1, duration: 300, easing: Easing.bezier(0.25, 0.46, 0.45, 0.94), useNativeDriver: true }).start();
        // Return currency images to original positions
        Animated.timing(poundTranslateT, { toValue: 0, duration: 300, easing: Easing.bezier(0.25, 0.46, 0.45, 0.94), useNativeDriver: true }).start();
        Animated.timing(dollarTranslateT, { toValue: 0, duration: 300, easing: Easing.bezier(0.25, 0.46, 0.45, 0.94), useNativeDriver: true }).start();
        Animated.timing(euroTranslateT, { toValue: 0, duration: 300, easing: Easing.bezier(0.25, 0.46, 0.45, 0.94), useNativeDriver: true }).start();
    };
    
    const onCard2In = () => {
        if (isExiting) return;
        Animated.timing(card2GlowT, { toValue: 1, duration: 1500, easing: Easing.bezier(0.22, 1, 0.36, 1), useNativeDriver: true }).start();
        Animated.timing(card2ImageScaleT, { toValue: 1.1, duration: 300, easing: Easing.bezier(0.25, 0.46, 0.45, 0.94), useNativeDriver: true }).start();
        // Move rocket up and right
        Animated.timing(rocketTranslateT, { toValue: 1, duration: 300, easing: Easing.bezier(0.25, 0.46, 0.45, 0.94), useNativeDriver: true }).start();
    };
    const onCard2Out = () => {
        if (isExiting) return;
        Animated.timing(card2GlowT, { toValue: 0, duration: 1500, easing: Easing.bezier(0.22, 1, 0.36, 1), useNativeDriver: true }).start();
        Animated.timing(card2ImageScaleT, { toValue: 1, duration: 300, easing: Easing.bezier(0.25, 0.46, 0.45, 0.94), useNativeDriver: true }).start();
        // Return rocket to original position
        Animated.timing(rocketTranslateT, { toValue: 0, duration: 300, easing: Easing.bezier(0.25, 0.46, 0.45, 0.94), useNativeDriver: true }).start();
    };
    
    const onCard3In = () => {
        if (isExiting) return;
        Animated.timing(card3GlowT, { toValue: 1, duration: 1500, easing: Easing.bezier(0.22, 1, 0.36, 1), useNativeDriver: true }).start();
        Animated.timing(card3ImageScaleT, { toValue: 1.1, duration: 300, easing: Easing.bezier(0.25, 0.46, 0.45, 0.94), useNativeDriver: true }).start();
        // Spread apart the community images
        Animated.timing(boyTranslateT, { toValue: 1, duration: 300, easing: Easing.bezier(0.25, 0.46, 0.45, 0.94), useNativeDriver: true }).start();
        Animated.timing(girlTranslateT, { toValue: 1, duration: 300, easing: Easing.bezier(0.25, 0.46, 0.45, 0.94), useNativeDriver: true }).start();
    };
    const onCard3Out = () => {
        if (isExiting) return;
        Animated.timing(card3GlowT, { toValue: 0, duration: 1500, easing: Easing.bezier(0.22, 1, 0.36, 1), useNativeDriver: true }).start();
        Animated.timing(card3ImageScaleT, { toValue: 1, duration: 300, easing: Easing.bezier(0.25, 0.46, 0.45, 0.94), useNativeDriver: true }).start();
        // Return community images to original positions
        Animated.timing(boyTranslateT, { toValue: 0, duration: 300, easing: Easing.bezier(0.25, 0.46, 0.45, 0.94), useNativeDriver: true }).start();
        Animated.timing(girlTranslateT, { toValue: 0, duration: 300, easing: Easing.bezier(0.25, 0.46, 0.45, 0.94), useNativeDriver: true }).start();
    };

    // Exit animation function
    const exitAnimation = (callback) => {
        setIsExiting(true);
        
        // Animate the main page out directly
        Animated.parallel([
            Animated.timing(pageOpacity, {
                toValue: 0,
                duration: 600,
                easing: Easing.in(Easing.cubic),
                useNativeDriver: true,
            }),
            Animated.timing(pageTranslateY, {
                toValue: 20,
                duration: 600,
                easing: Easing.in(Easing.cubic),
                useNativeDriver: true,
            }),
        ]).start(() => {
            if (callback) callback();
        });
    };

    // Navigation functions with exit animation
    const handleCard1Press = () => {
        exitAnimation(() => navigateTo('SameGameParlay'));
    };

    const handleCard2Press = () => {
        exitAnimation(() => navigateTo('SameGameParlay'));
    };

    const handleCard3Press = () => {
        setShowCommunityModal(true);
    };


    const handleCommunityModalClose = () => {
        setShowCommunityModal(false);
    };

    return (
        <View style={styles.container}>
            <View style={styles.background} />
            <View style={[styles.splashContainer, { 
                paddingTop: Platform.OS === 'web' ? (totalHeaderHeight + 32) : 16 
            }]}>
                <NavigationHeader />
                
                <Animated.View 
                    style={[
                        styles.contentContainer,
                        {
                            opacity: pageOpacity,
                            transform: [{ translateY: pageTranslateY }]
                        }
                    ]}
                >
                    <View style={styles.cardContainer}>
                        {/* Card 1 - Same Game Parlay */}
                        <TouchableOpacity 
                            style={styles.card} 
                            onPress={handleCard1Press}
                            activeOpacity={0.9}
                            {...(Platform.OS === 'web' ? { onMouseEnter: onCard1In, onMouseLeave: onCard1Out } : {})}
                        >
                            <GlassBackgroundGradient borderRadius={20} />
                            <GradientOverlay opacity={card1GlowT} borderRadius={20} />
                            <BlurView intensity={60} tint="dark" style={styles.cardBlur}>
                                <View style={styles.cardContent}>
                                    <Text style={styles.cardTitle}>SAME GAME{'\n'}PARLAY</Text>
                                    <Text style={styles.cardDescription}>
                                        Use the the world's most advanced math models and data to craft your optimized bets.
                                    </Text>
                                </View>
                                <Animated.Image 
                                    source={require('../assets/pngs/pound.png')} 
                                    style={[
                                        styles.parlayImage1,
                                        { 
                                            opacity: card1ImagesOpacity,
                                            transform: [
                                                { scale: card1ImagesScale },
                                                { scale: card1ImageScaleT }, 
                                                { scaleX: -1 },
                                                { 
                                                    translateX: poundTranslateT.interpolate({
                                                        inputRange: [0, 1],
                                                        outputRange: [0, 10]
                                                    })
                                                },
                                                { 
                                                    translateY: poundTranslateT.interpolate({
                                                        inputRange: [0, 1],
                                                        outputRange: [0, -5]
                                                    })
                                                }
                                            ] 
                                        }
                                    ]} 
                                />
                                <Animated.Image 
                                    source={require('../assets/pngs/dollar.png')} 
                                    style={[
                                        styles.parlayImage2,
                                        { 
                                            opacity: card1ImagesOpacity,
                                            transform: [
                                                { scale: card1ImagesScale },
                                                { scale: card1ImageScaleT },
                                                { 
                                                    translateX: dollarTranslateT.interpolate({
                                                        inputRange: [0, 1],
                                                        outputRange: [0, 4]
                                                    })
                                                },
                                                { 
                                                    translateY: dollarTranslateT.interpolate({
                                                        inputRange: [0, 1],
                                                        outputRange: [0, -10]
                                                    })
                                                }
                                            ] 
                                        }
                                    ]} 
                                />
                                <Animated.Image 
                                    source={require('../assets/pngs/euro.png')} 
                                    style={[
                                        styles.parlayImage3,
                                        { 
                                            opacity: card1ImagesOpacity,
                                            transform: [
                                                { scale: card1ImagesScale },
                                                { scale: card1ImageScaleT },
                                                { 
                                                    translateX: euroTranslateT.interpolate({
                                                        inputRange: [0, 1],
                                                        outputRange: [0, -3]
                                                    })
                                                },
                                                { 
                                                    translateY: euroTranslateT.interpolate({
                                                        inputRange: [0, 1],
                                                        outputRange: [0, 10]
                                                    })
                                                }
                                            ] 
                                        }
                                    ]} 
                                />
                            </BlurView>
                        </TouchableOpacity>

                        {/* Card 2 - Bet Opt Scraper */}
                        <TouchableOpacity 
                            style={styles.card} 
                            onPress={handleCard2Press}
                            activeOpacity={0.9}
                            {...(Platform.OS === 'web' ? { onMouseEnter: onCard2In, onMouseLeave: onCard2Out } : {})}
                        >
                            <GlassBackgroundGradient borderRadius={20} />
                            <GradientOverlay opacity={card2GlowT} borderRadius={20} />
                            <BlurView intensity={60} tint="dark" style={styles.cardBlur}>
                                <View style={styles.cardContent}>
                                    <Text style={styles.cardTitle}>BET opt{'\n'}SCRAPER</Text>
                                    <Text style={styles.cardDescription}>
                                        Customize legs, see live odds, and create the perfect bet slip.
                                    </Text>
                                            <Animated.Image 
                                                source={require('../assets/pngs/rocket.png')} 
                                                style={[
                                                    styles.scraperImage,
                                                    { 
                                                        opacity: card2ImagesOpacity,
                                                        transform: [
                                                            { scale: card2ImagesScale },
                                                            { scale: card2ImageScaleT }, 
                                                            { rotate: '-16deg' },
                                                            { 
                                                                translateX: rocketTranslateT.interpolate({
                                                                    inputRange: [0, 1],
                                                                    outputRange: [0, 10]
                                                                })
                                                            },
                                                            { 
                                                                translateY: rocketTranslateT.interpolate({
                                                                    inputRange: [0, 1],
                                                                    outputRange: [0, -10]
                                                                })
                                                            }
                                                        ] 
                                                    }
                                                ]} 
                                            />
                                </View>
                            </BlurView>
                        </TouchableOpacity>

                        {/* Card 3 - Our Community */}
                        <TouchableOpacity 
                            style={[styles.card, { zIndex: 1 }]} 
                            onPress={handleCard3Press}
                            activeOpacity={0.9}
                            {...(Platform.OS === 'web' ? { onMouseEnter: onCard3In, onMouseLeave: onCard3Out } : {})}
                        >
                            <GlassBackgroundGradient borderRadius={20} />
                            <GradientOverlay opacity={card3GlowT} borderRadius={20} />
                            <BlurView intensity={60} tint="dark" style={styles.cardBlur}>
                                <View style={styles.cardContent}>
                                    <Text style={styles.cardTitle}>OUR{'\n'}Community</Text>
                                    <Text style={styles.cardDescription}>
                                        Check out trending betslips, chat with community members, and more
                                    </Text>
                                    
                                    <View style={styles.communityImagesContainer}>
                                        <View style={{ position: 'relative', width: '100%', height: '100%' }}>
                                                    <Animated.Image 
                                                        source={require('../assets/pngs/boy.png')} 
                                                        style={[
                                                            styles.communityImage1,
                                                            { 
                                                                opacity: card3ImagesOpacity,
                                                                transform: [
                                                                    { scale: card3ImagesScale },
                                                                    { scale: card3ImageScaleT }, 
                                                                    { rotate: '-7deg' },
                                                                    { 
                                                                        translateX: boyTranslateT.interpolate({
                                                                            inputRange: [0, 1],
                                                                            outputRange: [0, -10]
                                                                        })
                                                                    },
                                                                    { 
                                                                        translateY: boyTranslateT.interpolate({
                                                                            inputRange: [0, 1],
                                                                            outputRange: [0, -10]
                                                                        })
                                                                    }
                                                                ] 
                                                            }
                                                        ]} 
                                                    />
                                                    <Animated.Image 
                                                        source={require('../assets/pngs/girl.png')} 
                                                        style={[
                                                            styles.communityImage2,
                                                            { 
                                                                opacity: card3ImagesOpacity,
                                                                transform: [
                                                                    { scale: card3ImagesScale },
                                                                    { scale: card3ImageScaleT }, 
                                                                    { scaleX: -1 }, 
                                                                    { rotate: '10deg' },
                                                                    { 
                                                                        translateX: girlTranslateT.interpolate({
                                                                            inputRange: [0, 1],
                                                                            outputRange: [0, -10]
                                                                        })
                                                                    },
                                                                    { 
                                                                        translateY: girlTranslateT.interpolate({
                                                                            inputRange: [0, 1],
                                                                            outputRange: [0, -5]
                                                                        })
                                                                    }
                                                                ] 
                                                            }
                                                        ]} 
                                                    />
                                        </View>
                                    </View>
                                </View>
                            </BlurView>
                        </TouchableOpacity>
                    </View>
                </Animated.View>
            </View>
            
            <CommunityModal
                visible={showCommunityModal}
                onClose={handleCommunityModalClose}
            />
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#131313',
        minHeight: '110vh',
    },
    background: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#131313',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.25,
        shadowRadius: 4,
    },
    splashContainer: {
        flex: 1,
        width: '100%',
        height: '100%',
        paddingHorizontal: Platform.OS === 'web' ? 48 : 24,
        paddingTop: Platform.OS === 'web' ? 32 : 16,
        paddingBottom: Platform.OS === 'web' ? 48 : 24,
        gap: 48,
    },
    mainContent: {
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        gap: 30,
        flex: 1,
        borderRadius: 20,
        backgroundColor: '#000',
        padding: 32,
        borderWidth: 1,
        borderBottomWidth: 2,
        borderTopColor: 'rgba(255,255,255,0.25)',
        borderLeftColor: 'rgba(255,255,255,0.18)',
        borderRightColor: 'rgba(255,255,255,0.12)',
        borderBottomColor: 'rgba(255,255,255,0.08)',
    },
    contentContainer: {
        width: '100%',
        height: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 30,
        paddingVertical: 24,
        display: 'flex',
    },
    cardContainer: {
        width: '100%',
        position: 'relative',
        flexDirection: 'row',
        gap: 30,
    },
    card: {
        flex: 1,
        position: 'relative',
        height: 720,
        backgroundColor: 'rgba(0, 0, 0, 0)',
        boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
        borderRadius: 20,
        flexDirection: 'column',
        alignItems: 'center',
        gap: 24,
        position: 'relative',
        borderWidth: 1,
        borderBottomWidth: 2,
        borderTopColor: 'rgba(255,255,255,0.25)',
        borderLeftColor: 'rgba(255,255,255,0.18)',
        borderRightColor: 'rgba(255,255,255,0.12)',
        borderBottomColor: 'rgba(255,255,255,0.08)',
    },
    cardBlur: {
        flex: 1,
        width: '100%',
        height: '100%',
        borderRadius: 20,
        padding: 20,
        justifyContent: 'center',
        alignItems: 'center',
    },
    cardContent: {
        alignSelf: 'stretch',
        flex: 1,
        padding: 20,
        flexDirection: 'column',
        justifyContent: 'flex-end',
        alignItems: 'flex-start',
        gap: 16,
        position: 'relative',
    },
    cardTitle: {
        color: 'rgba(255, 255, 255, 0.80)',
        fontSize: 40,
        fontFamily: 'ClimateCrisis',
        fontWeight: '400',
        textTransform: 'uppercase',
        lineHeight: 40,
    },
    cardDescription: {
        alignSelf: 'stretch',
        color: 'white',
        fontSize: 20,
        fontFamily: 'Slate',
        fontWeight: '500',
        lineHeight: 24,
    },
    cardImages: {
        flexDirection: 'row',
        gap: 20,
        alignItems: 'center',
    },
    cardImage1: {
        width: 135.64,
        height: 149.48,
    },
    cardImage2: {
        width: 122.50,
        height: 122.50,
    },
    cardImage3: {
        width: 111.50,
        height: 111.50,
    },
    cardImage4: {
        width: 408.83,
        height: 408.83,
        position: 'absolute',
        left: -35.32,
        top: -39.17,
    },
    cardImage5: {
        width: 309.79,
        height: 281.12,
        position: 'absolute',
        left: -38.41,
        top: 27.66,
    },
    cardImage6: {
        width: 264.71,
        height: 264.71,
        position: 'absolute',
        left: 416.53,
        top: 223.02,
    },
    rotatedImage: {
        transform: [{ rotate: '180deg' }],
    },
    rotatedImage15: {
        transform: [{ rotate: '-15deg' }],
    },
    rotatedImage7: {
        transform: [{ rotate: '-7deg' }],
    },
    rotatedImage177: {
        transform: [{ rotate: '-177deg' }],
    },
    communityImagesContainer: {
        position: 'absolute',
        top: 20,
        left: '50%',
        marginLeft: -100, // Half of total container width (300/2)
        width: 200,
        height: 200,
        alignSelf: 'center',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    },
    communityImage1: {
        height: 281,
        width: 309,
        position: 'absolute',
        left: -130,
        top: 50,
        transform: [{ rotate: '-7deg' }],
    },
    communityImage2: {
        height: 264,
        width: 264,
        position: 'absolute',
        right: -110,
        top: -20,
    },
    // New image styles for animations
    parlayImage1: {
        height: 122,
        width: 122,
        position: 'absolute',
        right: 120,
        top: 60,
        transform: [{ scaleX: -1 }],
    },
    parlayImage2: {
        height: 140,
        width: 140,
        position: 'absolute',
        right: 20,
        top: -40,
    },
    parlayImage3: {
        height: 111,
        width: 111,
        position: 'absolute',
        right: 15,
        top: 120,
    },
    scraperImage: {
        height: 410,
        width: 410,
        position: 'absolute',
        left: '50%',
        marginLeft: -205,
        top: -70,
    },
});

export default ExploreScreen;
